import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { getToleranceClasses, getToleranceClassesByRotor } from "./Data";

import AccuracyClassSelector from "../../../../components/Widgets/Tolerance/AccuracyClassSelector/AccuracyClassSelector";
import HelpInput from "../../../../components/Inputs/HelpInput/HelpInput.tsx";
import GeneralTolerance from "./GeneralTolerance.jsx";
import {
  getMassUnits,
  RadialSpeed,
  SpecificResidualDisbalanceByRadialSpeed,
} from "../../../../utils/helpers/tolerance/GeneralHelper.js";
import { useMeasureInUnits } from "../../../../hooks/useMeasureInUnits";

import { useTranslation } from "react-i18next";
import InputStack from "../../../../components/Styled/Stack/InputStack";
import PageContainerBox from "../../../../components/Styled/Box/PageContainerBox";
import SpecificDisbalanceDisplayer from "./SpecificDisbalanceDisplayer";

const GeneralToleranceWidget = (props) => {
  const { t } = useTranslation();

  const [toleranceClass, setToleranceClass] = useState(null);
  const [frequency, setFrequency] = useState(); // Default value for frequency

  const [isRotorSelected, setIsRotorSelected] = useState(true);

  const partMassUnits = useMemo(() => getMassUnits(t).slice(1, 4), [t]);
  const [
    massInKg,
    mass,
    setMass /* unitsOfMass, massUnit, handleMassUnitChange */,
  ] = useMeasureInUnits(null, partMassUnits[1]); // Default value for mass

  const selectorData = useMemo(() => {
    return isRotorSelected
      ? getToleranceClassesByRotor(t)
      : getToleranceClasses(t);
  }, [isRotorSelected, t]);

  const selectorDefaultLabel = useMemo(
    () =>
      isRotorSelected
        ? t(
            "pages.generalTolerance.components.toleranceClassSelector.options.byRotor.selectorLabel"
          )
        : t(
            "pages.generalTolerance.components.toleranceClassSelector.options.byToleranceClass.selectorLabel"
          ),
    [isRotorSelected, t]
  );

  const isDisabled = useMemo(() => {
    return !toleranceClass || toleranceClass?.value === 0;
  }, [toleranceClass]);

  const isUnbalanceDisabled = useMemo(() => {
    return isDisabled || !frequency || frequency === 0;
  }, [isDisabled, frequency]);

  const isGeneralToleranceDisabled = useMemo(() => {
    return isUnbalanceDisabled || !mass || mass === 0;
  }, [isDisabled, frequency, mass]);

  const handleFrequencyChange = useCallback((event) => {
    const value = +event.target.value;
    if (value >= 0) {
      setFrequency(value);
    } else {
      setFrequency(0);
    }
  }, []);

  const handleMassChange = useCallback((event) => {
    const value = +event.target.value;
    if (value >= 0) {
      setMass(value);
    } else {
      setMass(0);
    }
    // eslint-disable-next-line
  }, []);

  const handleIsRotorSelectedChange = useCallback(
    (event) => {
      const isChecked = Boolean(event.target.value === "true" ? true : false);
      setIsRotorSelected(isChecked);
      setToleranceClass(
        isChecked
          ? {
              label: t(
                "pages.generalTolerance.components.toleranceClassSelector.options.byToleranceClass.selectorLabel"
              ),
              value: 0,
            }
          : {
              label: t(
                "pages.generalTolerance.components.toleranceClassSelector.options.byRotor.selectorLabel"
              ),
              value: 0,
            }
      );
    },
    [t]
  );

  const radialSpeed = useMemo(() => {
    return RadialSpeed(frequency);
  }, [frequency]);

  const specificResidualDisbalance = useMemo(
    () =>
      toleranceClass
        ? SpecificResidualDisbalanceByRadialSpeed(
            toleranceClass.value,
            radialSpeed
          )
        : 0,
    [toleranceClass, radialSpeed]
  );

  return (
    <PageContainerBox>
      <Typography variant="h4" sx={{ my: 2 }}>
        {t("pages.generalTolerance.parameters.main")}
      </Typography>
      <Typography variant="h6" gutterBottom>
        {t("pages.generalTolerance.components.toleranceClassSelector.title")}
      </Typography>
      <RadioGroup
        row
        value={isRotorSelected}
        onChange={handleIsRotorSelectedChange}
      >
        <FormControlLabel
          value={false}
          control={<Radio />}
          label={t(
            "pages.generalTolerance.components.toleranceClassSelector.options.byToleranceClass.label"
          )}
        />
        <FormControlLabel
          value={true}
          control={<Radio />}
          label={t(
            "pages.generalTolerance.components.toleranceClassSelector.options.byRotor.label"
          )}
        />
      </RadioGroup>
      <AccuracyClassSelector
        toleranceClasses={selectorData}
        onChange={setToleranceClass}
        defaultLabel={selectorDefaultLabel}
        isSearchable={isRotorSelected}
        value={toleranceClass}
      />
      {/* Inputs for Frequency and Mass */}
      <Typography variant="h6" sx={{ my: 2 }}>
        {t("pages.generalTolerance.components.frequency.title")}
      </Typography>
      <InputStack>
        <HelpInput
          label={<div>v, {t("units.rotationSpeed.rpm")}</div>}
          type="number"
          variant="outlined"
          fullWidth
          value={frequency || ""} // Bind value to state
          onChange={handleFrequencyChange} // Use callback for handling input change
          helpButtonText={t(
            "pages.generalTolerance.components.frequency.frequency.helpButtonText"
          )}
          disabled={isDisabled}
        />
      </InputStack>
      <SpecificDisbalanceDisplayer
        specificResidualDisbalance={specificResidualDisbalance}
        isDisabled={isUnbalanceDisabled}
      />
      {!isUnbalanceDisabled && (
        <>
          <Typography variant="h4" sx={{ my: 2 }}>
            {t("pages.generalTolerance.parameters.additional")}
          </Typography>
          <Typography variant="h6" sx={{ my: 2 }}>
            {t("pages.generalTolerance.components.mass.title")}
          </Typography>
          <InputStack>
            <HelpInput
              label={`m, ${t("units.mass.kg")}`}
              type="number"
              variant="outlined"
              fullWidth
              value={mass || ""} // Bind value to state
              onChange={handleMassChange} // Use callback for handling input change
              helpButtonText={t(
                "pages.generalTolerance.components.mass.mass.helpButtonText"
              )}
              disabled={isUnbalanceDisabled}
            />
          </InputStack>
        </>
      )}
      <GeneralTolerance
        toleranceClass={toleranceClass}
        isDisabled={isGeneralToleranceDisabled}
        mass={massInKg}
        specificResidualDisbalance={specificResidualDisbalance}
        radialSpeed={radialSpeed}
      />
    </PageContainerBox>
  );
};

export default GeneralToleranceWidget;
